/**
 * Image management hook
 */

import { useState, useCallback } from 'react';
import { mediaApi } from '../services/api';
import type { Media, PaginatedResponse, UploadResponse } from '../../../../packages/shared/src/types';

interface UseImagesReturn {
  images: Media[];
  isLoading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  fetchImages: (page?: number) => Promise<void>;
  uploadImage: (file: File, originalName: string) => Promise<{ success: boolean; error?: string; data?: UploadResponse }>;
  deleteImage: (id: string) => Promise<{ success: boolean; error?: string }>;
  getShareUrl: (id: string) => Promise<{ success: boolean; url?: string; error?: string }>;
}

export function useImages(): UseImagesReturn {
  const [images, setImages] = useState<Media[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchImages = useCallback(async (pageNum: number = 1) => {
    setIsLoading(true);
    setError(null);

    const response = await mediaApi.list(pageNum, 20);
    
    if (response.success && response.data) {
      setImages(response.data.items);
      setTotal(response.data.total);
      setPage(response.data.page);
      setTotalPages(response.data.totalPages);
    } else {
      setError(response.error?.message || 'Failed to fetch images');
    }
    
    setIsLoading(false);
  }, []);

  const uploadImage = useCallback(async (file: File, originalName: string) => {
    const response = await mediaApi.upload(file, originalName);
    
    if (response.success && response.data) {
      await fetchImages(page);
      return { success: true, data: response.data };
    }
    
    return {
      success: false,
      error: response.error?.message || 'Upload failed',
    };
  }, [fetchImages, page]);

  const deleteImage = useCallback(async (id: string) => {
    const response = await mediaApi.delete(id);
    
    if (response.success) {
      await fetchImages(page);
      return { success: true };
    }
    
    return {
      success: false,
      error: response.error?.message || 'Delete failed',
    };
  }, [fetchImages, page]);

  const getShareUrl = useCallback(async (id: string) => {
    const response = await mediaApi.getShareUrl(id);
    
    if (response.success && response.data) {
      return { success: true, url: response.data.url };
    }
    
    return {
      success: false,
      error: response.error?.message || 'Failed to get share URL',
    };
  }, []);

  return {
    images,
    isLoading,
    error,
    total,
    page,
    totalPages,
    fetchImages,
    uploadImage,
    deleteImage,
    getShareUrl,
  };
}

export default useImages;
